// frontend/src/pages/devioz/Perfil.tsx
import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { getMode, setAutoDark, onThemeChange, type ThemeMode } from '../../services/theme';

/**
 * Secciones:
 * - Datos de la cuenta (PUT /api/users/me)
 * - Preferencias (modo oscuro) – theme.ts
 * - Seguridad (POST /api/auth/change-password)
 * - Sesión (logout)
 */

type RoleLike = { code?: string; name?: string } | string;

type LocalUser = {
  id?: number;
  first_name?: string;
  last_name?: string;
  name?: string; // compat
  email?: string;
  roles?: RoleLike[];
  company_id?: number;
};

export default function Perfil() {
  // Ahora el contexto expone setUser (ver AuthContext.tsx)
  const { user: ctxUser, setUser, logout } = useAuth();

  // Usuario base (de contexto o storage)
  const userFromStorage = useMemo<LocalUser | null>(() => {
    if (ctxUser) return ctxUser as any;
    const raw = localStorage.getItem('user') ?? sessionStorage.getItem('user');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }, [ctxUser]);

  // Estado editable del perfil
  const [uFirst, setUFirst] = useState(userFromStorage?.first_name || '');
  const [uLast, setULast] = useState(userFromStorage?.last_name || '');
  const [uEmail, setUEmail] = useState(userFromStorage?.email || '');

  // Re-sincroniza inputs si cambia el usuario del contexto/algo en storage
  useEffect(() => {
    setUFirst(userFromStorage?.first_name || '');
    setULast(userFromStorage?.last_name || '');
    setUEmail(userFromStorage?.email || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFromStorage?.first_name, userFromStorage?.last_name, userFromStorage?.email]);

  // Preferencias de tema
  const [autoDark, setAutoDarkState] = useState<boolean>(getMode() === 'auto');
  const [modeText, setModeText] = useState<string>(
    getMode() === 'auto' ? 'Modo oscuro activado' : 'Modo claro activado'
  );

  // Seguridad
  const [currPass, setCurrPass] = useState('');
  const [newPass, setNewPass] = useState('');
  const [repPass, setRepPass] = useState('');

  // Feedback UI
  const [saving, setSaving] = useState(false);
  const [passLoading, setPassLoading] = useState(false);
  const [msgOk, setMsgOk] = useState<string>('');
  const [msgErr, setMsgErr] = useState<string>('');
  const [msgPassOk, setMsgPassOk] = useState<string>('');
  const [msgPassErr, setMsgPassErr] = useState<string>('');

  // Sincroniza texto/estado cuando cambia el modo
  useEffect(() => {
    const unsub = onThemeChange((m: ThemeMode) => {
      setAutoDarkState(m === 'auto');
      setModeText(m === 'auto' ? 'Modo oscuro activado' : 'Modo claro activado');
    });
    return () => unsub();
  }, []);

  const handleToggleTheme = (e: React.ChangeEvent<HTMLInputElement>) => {
    const enabled = e.target.checked;
    setAutoDark(enabled); // aplica/retira clases y persiste (theme.ts)
    setAutoDarkState(enabled);
    setModeText(enabled ? 'Modo oscuro activado' : 'Modo claro activado');
  };

  /** Guarda datos de perfil */
  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsgOk('');
    setMsgErr('');
    setSaving(true);
    try {
      const payload = { first_name: uFirst, last_name: uLast, email: uEmail };
      await api.put('/api/users/me', payload); // ajusta ruta si tu backend difiere

      // Merge y persistencia global (context + storage: lo hace el provider)
      const mergedUser: LocalUser = { ...(userFromStorage || {}), ...payload };
      setUser?.(mergedUser as any);

      setMsgOk('Perfil actualizado correctamente.');
    } catch (err: any) {
      setMsgErr(err?.response?.data?.message || err?.message || 'No se pudo actualizar el perfil.');
    } finally {
      setSaving(false);
    }
  };

  /** Cambia contraseña */
  const handleChangePass = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsgPassOk('');
    setMsgPassErr('');
    if (!currPass || !newPass) {
      setMsgPassErr('Completa los campos requeridos.');
      return;
    }
    if (newPass !== repPass) {
      setMsgPassErr('La nueva contraseña y su confirmación no coinciden.');
      return;
    }
    setPassLoading(true);
    try {
      await api.post('/api/auth/change-password', {
        current_password: currPass,
        new_password: newPass,
      });
      setMsgPassOk('Contraseña cambiada correctamente.');
      setCurrPass('');
      setNewPass('');
      setRepPass('');
    } catch (err: any) {
      setMsgPassErr(err?.response?.data?.message || err?.message || 'No se pudo cambiar la contraseña.');
    } finally {
      setPassLoading(false);
    }
  };

  const mainRole = useMemo(() => {
    const roles = (userFromStorage?.roles || []) as RoleLike[];
    if (!roles.length) return '—';
    const first = roles[0];
    return typeof first === 'string' ? first : first?.code || first?.name || '—';
  }, [userFromStorage]);

  return (
    <div className="container-fluid py-4">
      <h1 className="page-header mb-3">Perfil</h1>

      {/* ===== Datos de la cuenta ===== */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <h5 className="fw-semibold mb-3">Datos de la cuenta</h5>

          {msgOk && <div className="alert alert-success py-2">{msgOk}</div>}
          {msgErr && <div className="alert alert-danger py-2">{msgErr}</div>}

          <form onSubmit={handleSaveProfile} className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Nombres</label>
              <input
                type="text"
                className="form-control"
                value={uFirst}
                onChange={(e) => setUFirst(e.target.value)}
                placeholder="Tus nombres"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Apellidos</label>
              <input
                type="text"
                className="form-control"
                value={uLast}
                onChange={(e) => setULast(e.target.value)}
                placeholder="Tus apellidos"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-control"
                value={uEmail}
                onChange={(e) => setUEmail(e.target.value)}
                placeholder="tu@email.com"
              />
            </div>

            <div className="col-md-4">
              <label className="form-label">Rol principal</label>
              <input type="text" className="form-control" value={mainRole} disabled />
            </div>

            <div className="col-12 d-flex gap-2 mt-2">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ===== Preferencias (modo oscuro) ===== */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body d-flex align-items-center justify-content-between">
          <div>
            <div className="fw-medium">Modo oscuro automático</div>
            <div className="text-muted small">Cambia toda la interfaz y recuerda tu preferencia.</div>
            <div className="mt-2">
              <span className={`badge ${autoDark ? 'bg-primary' : 'bg-secondary'}`}>{modeText}</span>
            </div>
          </div>
          <div className="form-check form-switch fs-5 m-0">
            <input
              id="autoDarkSwitch"
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={autoDark}
              onChange={handleToggleTheme}
              aria-label="Activar modo oscuro automático"
            />
          </div>
        </div>
      </div>

      {/* ===== Seguridad: cambiar contraseña ===== */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <h5 className="fw-semibold mb-3">Seguridad</h5>

          {msgPassOk && <div className="alert alert-success py-2">{msgPassOk}</div>}
          {msgPassErr && <div className="alert alert-danger py-2">{msgPassErr}</div>}

          <form onSubmit={handleChangePass} className="row g-3">
            <div className="col-md-4">
              <label className="form-label">Contraseña actual</label>
              <input
                type="password"
                className="form-control"
                value={currPass}
                onChange={(e) => setCurrPass(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Nueva contraseña</label>
              <input
                type="password"
                className="form-control"
                value={newPass}
                onChange={(e) => setNewPass(e.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Repetir nueva contraseña</label>
              <input
                type="password"
                className="form-control"
                value={repPass}
                onChange={(e) => setRepPass(e.target.value)}
                autoComplete="new-password"
              />
            </div>

            <div className="col-12 d-flex gap-2 mt-2">
              <button type="submit" className="btn btn-outline-primary" disabled={passLoading}>
                {passLoading ? 'Actualizando…' : 'Cambiar contraseña'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* ===== Sesión ===== */}
      <div className="card border-0 shadow-sm">
        <div className="card-body d-flex align-items-center justify-content-between">
          <div>
            <div className="fw-medium">Sesión</div>
            <div className="text-muted small">Cierra tu sesión de manera segura en este dispositivo.</div>
          </div>
          <button
            type="button"
            className="btn btn-outline-danger"
            onClick={() => {
              try {
                logout?.();
              } catch {
                /* no-op */
              }
            }}
          >
            Cerrar sesión
          </button>
        </div>
      </div>
    </div>
  );
}
